import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    state: '',
    serviceType: 'Armed Security Officers',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        state: formData.state,
        serviceType: formData.serviceType,
        message: formData.message,
      });

      const response = await fetch('https://readdy.ai/api/form/d5sdvvc6pqudvsuu53hg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          state: '',
          serviceType: 'Armed Security Officers',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="relative pt-32 pb-20 bg-dark-900">
        <div className="absolute inset-0">
          <img
            src="https://cdn.prod.website-files.com/68b07a689aa678cd7b4261c6/68c47978726ad1c161738d71_p8.avif"
            alt="Contact Us"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 font-display">
            Get A Quote Today
          </h1>
          <p className="text-xl text-dark-100 max-w-3xl mx-auto">
            Whether you're a business, organization, or individual looking for professional security services, we make it easy to get a personalized quote.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <img
                src="https://cdn.prod.website-files.com/68b07a689aa678cd7b4261c6/68d5938f8c7e6034c44f630a_AGS_3905%201.avif"
                alt="Contact American Guard Services"
                className="w-full h-full object-cover rounded-lg"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-dark-900 mb-6 font-display">
                Request a Free Consultation
              </h2>
              <p className="text-dark-600 text-lg mb-8">
                We'd love to know more about how we can help you. Request a free quote today with your information and our team will review your request promptly.
              </p>

              <div className="space-y-4 mb-8">
                <a
                  href="mailto:marketing@americanguardservices.com"
                  className="flex items-center text-dark-700 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-mail-line text-2xl text-primary-600 mr-4"></i>
                  <span>marketing@americanguardservices.com</span>
                </a>
                <a
                  href="tel:1-800-662-7372"
                  className="flex items-center text-dark-700 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-phone-line text-2xl text-primary-600 mr-4"></i>
                  <span>1-800-662-7372</span>
                </a>
                <div className="flex items-start text-dark-700">
                  <i className="ri-map-pin-line text-2xl text-primary-600 mr-4 mt-1"></i>
                  <span>1125 W 190th St.<br />Los Angeles, CA 90248</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-dark-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-dark-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-dark-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="john.doe@company.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-dark-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-dark-700 mb-2">
                      State *
                    </label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      placeholder="CA"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-dark-700 mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Your Company Name"
                  />
                </div>

                <div>
                  <label htmlFor="serviceType" className="block text-sm font-medium text-dark-700 mb-2">
                    Service Required *
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option>Armed Security Officers</option>
                    <option>Unarmed Security Officers</option>
                    <option>Event Security</option>
                    <option>Patrol & Mobile Surveillance</option>
                    <option>Construction Site Security</option>
                    <option>Loss Prevention Security</option>
                    <option>Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-dark-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your security needs..."
                  ></textarea>
                  <p className="text-xs text-dark-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    Thank you! Your request has been submitted successfully. We'll contact you soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    There was an error submitting your request. Please try again or contact us directly.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-primary-500 text-dark-900 rounded-lg text-base font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>

                <p className="text-xs text-dark-500">
                  By submitting this form, you agree to allow American Guard Services to collect and process your information.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-dark-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-dark-900 mb-4 font-display">
              Serving Nationwide
            </h2>
            <p className="text-dark-600 text-lg">
              Headquartered in Los Angeles, American Guard Services provides trusted security solutions across 33 states.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-sm border border-dark-200">
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3314.2847!2d-118.3009!3d33.8536!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDUxJzEzLjAiTiAxMTjCsDE3JzU1LjIiVw!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="American Guard Services Location"
                className="rounded-lg"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;